import { format } from 'date-fns';
import normalize from 'json-api-normalizer';

import { getUTCDate } from '../../shared/utilities/date';

import {
    Contract,
    ContractId,
    ContractResponseFromAPI,
    LegalInformationResponseFromAPI,
    NormalizedContract,
    PostContractBody,
    ProofDocument,
    ProofDocumentFromAPI,
    QuotePricing,
    QuotePricingResponseFromAPI,
    SerializedLegalInformation,
    Traveler,
    TravelInformation,
    FilledQuotePricing,
    QuotePricingPayloadForAPI,
    PaymentScheduleViewModel,
    PaymentScheduleBody,
    InstalmentViewModel,
    InstalmentBody,
} from './types';

import { currencySubunit } from './index';

// TODO: Rename to normalize and adapt types in consequences
export function serializeContractFromAPI(json: ContractResponseFromAPI): Contract {
    const dossierId = json.data.id;
    const contractSerialized: NormalizedContract = normalize(json);
    const contractAttributes = contractSerialized.contract[dossierId].attributes;

    return {
        validatedAt: contractAttributes.validatedAt,
        price: contractAttributes.price,
        legalInformation: contractAttributes.legalInformation,
        agencyParty: contractAttributes.agencyParty,
        proposal: contractAttributes.proposal,
        vouchers: contractAttributes.vouchers,
        paymentSchedule: contractAttributes.paymentSchedule,
        deferAgreement: contractAttributes.deferAgreement,
        travelersInformation: contractAttributes.travelersInformation,
        currencies: json.data.meta.currencies,
        travelDates: {
            arrivalDate: getUTCDate(contractAttributes.travelDates.arrivalDate),
            departureDate: getUTCDate(contractAttributes.travelDates.departureDate),
        },
    };
}

export function serializePaymentScheduleForAPI(
    paymentSchedule: PaymentScheduleViewModel,
): PaymentScheduleBody {
    if (paymentSchedule.currency === undefined) {
        throw new Error('Currency should be filled');
    }
    return {
        currency: paymentSchedule.currency,
        instalments: paymentSchedule.depositInstalments
            .concat(paymentSchedule.balanceInstalments)
            .map((instalment) => serializeInstalmentForAPI(instalment)),
    };
}

export function serializeInstalmentForAPI(instalment: InstalmentViewModel): InstalmentBody {
    return {
        label: instalment.label,
        type: instalment.type,
        price: {
            amount: Math.round((instalment.price.amount as number) * currencySubunit).toString(),
            currency: instalment.price.currency,
        },
        payer_name: instalment.payerName,
        due_date: format(instalment.dueDate, 'yyyy-MM-dd'),
    };
}

export function serializeContractForAPI(
    contractId: ContractId,
    travelInformation: TravelInformation,
    travelers: Traveler[],
): PostContractBody {
    return {
        contract_id: contractId,
        travel_dates: {
            arrival_date: format(travelInformation.arrivalDate as Date, 'yyyy-MM-dd'),
            departure_date: format(travelInformation.departureDate as Date, 'yyyy-MM-dd'),
        },
        travelers_information: travelers.map((traveler) => {
            return {
                first_name: traveler.firstName,
                last_name: traveler.lastName,
                birth_date: traveler.birthDate,
                country: traveler.country,
            };
        }),
        proposal: travelInformation.proposal as string | File,
        legal_information: {
            reference: travelInformation.legalInformationReference as string,
        },
    };
}

export function serializeLegalInformationFromAPI(
    json: LegalInformationResponseFromAPI,
): SerializedLegalInformation[] {
    return json.data.map((legal) => {
        return {
            id: legal.id,
            reference: legal.attributes.reference,
            language: legal.attributes.language,
            destination: legal.meta.destination,
        };
    });
}

export function serializeProofDocumentFromAPI(json: ProofDocumentFromAPI): ProofDocument {
    const [{ name, path }] = json.data;
    return {
        name,
        url: path,
    };
}

export function serializeQuotePricing(json: QuotePricingResponseFromAPI): QuotePricing {
    const pricing = json.data;
    return {
        id: pricing.id,
        localAgencyCurrency: pricing.localAgencyCurrency,
        fees: {
            domesticFlights: pricing.prices['domestic-flights'],
            internationalFlights: pricing.prices['international-flights'],
            landServices: pricing.prices['land-services'],
        },
        proofDocuments: pricing.proofDocuments,
    };
}

export function serializeQuotePricingForAPI(
    contractId: ContractId,
    localAgencyQuotePrices: FilledQuotePricing,
    proofOfDocuments: ProofDocument[],
): QuotePricingPayloadForAPI {
    return {
        localAgencyCurrency: localAgencyQuotePrices.currency,
        localAgencyDomesticFlightsPrice: localAgencyQuotePrices.domesticFlightsPrice.toString(),
        localAgencyInternationalFlightsPrice:
            localAgencyQuotePrices.internationalFlightsPrice.toString(),
        localAgencyLandServicesPrice: localAgencyQuotePrices.landServicesPrice.toString(),
        proofDocuments: proofOfDocuments,
        quoteId: parseInt(contractId, 10),
    };
}
